import { CheckCircle2, Loader2 } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useScenarioRefreshStatus } from "../../hooks/useScenarioRefreshStatus";

interface ScenarioRefreshBannerProps {
	/** Only render / poll while on the Scenarios or Scenario detail pages. */
	enabled: boolean;
}

const COMPLETE_VISIBLE_MS = 8_000;

/**
 * Banner shown while one or more scenarios are being refreshed. Mirrors the
 * ForecastRefreshBanner conventions (amber in-progress, green complete). Wording
 * differs for a single scenario (named) versus multiple scenarios. Renders and
 * polls only on the scenario pages — never globally.
 */
export const ScenarioRefreshBanner: React.FC<ScenarioRefreshBannerProps> = ({
	enabled,
}) => {
	const { updating, count } = useScenarioRefreshStatus(enabled);
	const [showComplete, setShowComplete] = useState(false);
	const prevCount = useRef(0);
	const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// When the count drops from >0 to 0, briefly show a completion message.
	useEffect(() => {
		if (prevCount.current > 0 && count === 0) {
			setShowComplete(true);
			if (hideTimer.current) clearTimeout(hideTimer.current);
			hideTimer.current = setTimeout(
				() => setShowComplete(false),
				COMPLETE_VISIBLE_MS,
			);
		}
		if (count > 0 && showComplete) setShowComplete(false);
		prevCount.current = count;
	}, [count, showComplete]);

	useEffect(() => {
		return () => {
			if (hideTimer.current) clearTimeout(hideTimer.current);
		};
	}, []);

	if (!enabled) return null;

	if (count > 0) {
		const message =
			count === 1
				? `Refreshing "${updating[0]?.name ?? "scenario"}"`
				: `${count} scenarios are being refreshed`;
		return (
			<div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-sm text-amber-800">
				<Loader2 className="h-4 w-4 text-amber-600 animate-spin flex-shrink-0" />
				<span>
					<strong>{message}</strong>
					<span className="font-normal text-amber-700">
						{" "}
						· Re-pinning to the latest base forecast
					</span>
				</span>
			</div>
		);
	}

	if (showComplete) {
		return (
			<div className="w-full bg-green-50 border-b border-green-200 px-4 py-2 flex items-center gap-2 text-sm text-green-800">
				<CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
				<span>
					<strong>Scenario refresh complete</strong>
					<span className="font-normal text-green-700">
						{" "}
						· Now pinned to the latest base forecast
					</span>
				</span>
			</div>
		);
	}

	return null;
};
